"use client"

import styled from "@emotion/styled"
import { useEffect, useRef, useState } from "react"
import { extractProperty, replaceRichTextLinks } from "utils"
import { useLandingAnalytics } from "../analytics"
import { JsonLd, buildFaqSchema } from "utils/structuredData"

type Props = {
  data: any
}

// --- Reveal-once-on-scroll hook (AC: initial animation fires once per page load) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.15 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [inView])

  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 40px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 90px 120px;
  }
`

const Inner = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: start;
`

/* Figma centres the title cluster on both breakpoints (`cross=CENTER`, and the
   text frames sit centred in the 1440/375 frames). The accordion items below
   stay start-aligned, so the centring belongs here rather than on Inner —
   which is why setting it on Inner previously did not take (ASH-1777). */
const Header = styled.div<{ $in: boolean }>`
  text-align: center;
  margin-bottom: 24px;
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "16px")});
  transition: opacity 0.5s ease, transform 0.5s ease;

  ${(props) => props.theme.breakpoints.up("md")} {
    margin-bottom: 32px;
  }
`

const Headline = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--comp-heading);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const Subtitle = styled.p`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  color: var(--comp-text);
  margin: 8px 0 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 24px;
    font-weight: 500;
  }
`

const RunningText = styled.p`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.6;
  color: var(--comp-text);
  margin: 12px 0 0;
`

/* list appears after the header (AC: header first, then question list) */
const List = styled.div<{ $in: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "16px")});
  transition: opacity 0.5s ease 0.2s, transform 0.5s ease 0.2s;
`

/* Each question sits in its own bordered, rounded card (ASH-1734, matches Figma). */
const Item = styled.div`
  border: 1px solid color-mix(in srgb, var(--inner-text, var(--comp-text)) 20%, transparent);
  border-radius: 16px;
  padding: 0 20px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0 32px;
  }
`

const QuestionButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  /* touch-friendly hit area */
  padding: 20px 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: start;
  color: var(--inner-heading, var(--comp-heading));

  &:focus-visible {
    outline: 2px dashed var(--inner-text, var(--comp-text));
    outline-offset: 2px;
  }
`

const QuestionText = styled.h3`
  font-size: 18px;
  font-weight: 400;
  line-height: 1.5;
  letter-spacing: -0.36px;
  flex: 1;
  margin: 0;
`

/* + / − toggle drawn with two bars; the vertical bar collapses when open */
const ToggleIcon = styled.span<{ $open: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  &::before,
  &::after {
    content: "";
    position: absolute;
    background-color: var(--inner-heading, var(--comp-heading));
    transition: transform 0.3s ease;
  }
  /* horizontal bar */
  &::before {
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    transform: translateY(-50%);
  }
  /* vertical bar — hidden when open */
  &::after {
    left: 50%;
    top: 0;
    width: 2px;
    height: 100%;
    transform: translateX(-50%) scaleY(${({ $open }) => ($open ? 0 : 1)});
  }
`

/* grid-rows 0fr→1fr gives a smooth height animation without measuring content */
const AnswerWrapper = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${({ $open }) => ($open ? "1fr" : "0fr")};
  transition: grid-template-rows 0.3s ease;
`

const AnswerInner = styled.div`
  overflow: hidden;
`

/* Answer copy is 18/27 on mobile but steps DOWN to 16/24 on desktop in Figma —
   QA measured the desktop value (ASH-1780). */
const Answer = styled.div`
  font-size: 18px;
  font-weight: 400;
  line-height: 27px;
  color: var(--inner-text, var(--comp-text));

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 16px;
    line-height: 24px;
  }
  /* divider between the question and its answer, shown only when expanded */
  border-top: 1px solid color-mix(in srgb, var(--inner-text, var(--comp-text)) 20%, transparent);
  padding-top: 20px;
  padding-bottom: 20px;

  a {
    color: var(--inner-heading, var(--comp-heading));
    text-decoration: underline;
  }

  p {
    margin: 0 0 8px;
  }
`

// --- Types & parsing ---

type FaqItem = {
  question: string
  answer: string
}

function parseItem(block: any): FaqItem {
  return {
    question: (extractProperty(block, "question") as string) || "",
    answer: (extractProperty(block, "answer") as string) || "",
  }
}

// --- Main Component ---

function Faq({ data }: Props) {
  const { ref, inView } = useInViewOnce<HTMLDivElement>()
  const analytics = useLandingAnalytics()
  const [openIndex, setOpenIndex] = useState<number | null>(null) // all closed by default per AC

  const headline = extractProperty(data, "headline") as string | ""
  const subtitle = extractProperty(data, "subtitle") as string | ""
  const text = extractProperty(data, "text") as string | ""
  const itemBlocks = extractProperty(data, "items") as any[] | ""
  const items = (Array.isArray(itemBlocks) ? itemBlocks : [])
    .map(parseItem)
    .filter((item) => item.question)

  if (!headline && items.length === 0) return null

  const faqSchema = buildFaqSchema(items)

  return (
    <Section ref={ref}>
      <JsonLd schema={faqSchema} />
      <Inner>
        <Header $in={inView}>
          {headline && <Headline>{headline}</Headline>}
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
          {text && <RunningText>{text}</RunningText>}
        </Header>
        {items.length > 0 && (
          <List $in={inView}>
            {items.map((item, i) => {
              const open = openIndex === i
              return (
                <Item key={i}>
                  <QuestionButton
                    onClick={() => {
                      // spec tracks opening a question, not collapsing it
                      if (!open) analytics.faqClick(item.question)
                      setOpenIndex(open ? null : i)
                    }}
                    aria-expanded={open}
                    aria-controls={`faq-answer-${i}`}
                    id={`faq-question-${i}`}
                  >
                    <QuestionText>{item.question}</QuestionText>
                    <ToggleIcon $open={open} aria-hidden />
                  </QuestionButton>
                  <AnswerWrapper
                    $open={open}
                    role="region"
                    id={`faq-answer-${i}`}
                    aria-labelledby={`faq-question-${i}`}
                  >
                    <AnswerInner>
                      {item.answer && (
                        <Answer
                          dangerouslySetInnerHTML={{
                            __html: replaceRichTextLinks(item.answer, true),
                          }}
                        />
                      )}
                    </AnswerInner>
                  </AnswerWrapper>
                </Item>
              )
            })}
          </List>
        )}
      </Inner>
    </Section>
  )
}

export default Faq
